import { useEffect, useState } from "react";
import ICatalog_ItemUI from "../../../../../models/ICatalog_ItemUI";
import IKit_Builder_Option from "../../../../../models/IKit_Builder_Option";
import OwLabel from "../../../../shared/OwLabel/OwLabel";
import OwButton from "../../../../shared/OwButton/OwButton";
import INewKIt_Builder_Option from "../../../../../models/INewKIt_Builder_Option";
import { CatalogItemKitBuilderOption } from "./CatalogItemKitBuilderOption/CatalogItemKitBuilderOption";
import IKit_Builder_Option_Item from "../../../../../models/IKit_Builder_Option_Item";
import IKit_Builder_OptionUI from "../../../../../models/IKit_Builder_OptionUI";
import { v4 } from "uuid";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    DragStartEvent,
    DragOverlay,
    DragMoveEvent,
} from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import "./CatalogItemKitBuilder.scss";

type Props = {
    catalogItem: ICatalog_ItemUI;
    catalogGuid: string;
    updateFn: (options: (IKit_Builder_OptionUI | Partial<IKit_Builder_OptionUI>)[]) => void;
};

export const CatalogItemKitBuilder = (props: Props) => {
    // Configure sensors for both pointer (mouse/touch) and keyboard accessibility
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8, // Start dragging after moving 8px to avoid accidental drags
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const getOptions = () => {
        const options = (props.catalogItem.Kit_Builder_Options || []).map((option) => {
            const uuid = v4();

            return { ...option, uuid };
        });

        return options;
    };

    const [kitBuilderOptions, setKitBuilderOptions] = useState<(IKit_Builder_OptionUI | Partial<IKit_Builder_OptionUI>)[]>(getOptions());
    const [openAll, setOpenAll] = useState(false);
    const [activeId, setActiveId] = useState<string | null>(null);
    const [activeOption, setActiveOption] = useState<IKit_Builder_OptionUI | Partial<IKit_Builder_OptionUI> | null>(null);

    useEffect(() => {
        setKitBuilderOptions(getOptions());
    }, [props.catalogItem.Kit_Builder_Options]);

    useEffect(() => {
        if (kitBuilderOptions.length) {
            props.updateFn(kitBuilderOptions);
        }
    }, [kitBuilderOptions]);

    const generateKitBuilderOption = () => {
        if (props.catalogItem) {
            const newOption: Partial<IKit_Builder_OptionUI> = {
                catalog_item_id: props.catalogItem?.id,
                allow_repeats: false,
                name: "",
                max_allowed: 0,
                min_required: 0,
                Kit_Builder_Option_Items: [],
                uuid: v4(),
            };

            return newOption;
        }
    };

    const addKitOption = () => {
        const newOption = generateKitBuilderOption();

        if (newOption) {
            setKitBuilderOptions([newOption, ...kitBuilderOptions]);
        }
    };

    const updateOption = (option: IKit_Builder_OptionUI | Partial<IKit_Builder_OptionUI>) => {
        // if the delete flag is set, and the option has no guid, then splice it out.
        if (option.deleteFlag && !option.id) {
            setKitBuilderOptions(kitBuilderOptions.filter((existingOption) => existingOption.uuid !== option.uuid));
            return;
        } else {
            const updatedOptions = kitBuilderOptions.map((existingOption) => {
                if (existingOption.uuid === option.uuid) {
                    return option;
                } else {
                    return existingOption;
                }
            });

            setKitBuilderOptions(updatedOptions);
        }
    };

    const openAllFn = () => {
        setOpenAll(!openAll);
    };

    // Handle drag start - capture the active item
    const handleDragStart = (event: DragStartEvent) => {
        const { active } = event;
        setActiveId(active.id as string);

        // Find the active option to display in drag overlay
        const option = kitBuilderOptions.find((option) => option.uuid === active.id);
        if (option) {
            setActiveOption(option);
        }
    };

    // Handle drag end - reorder the list
    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            setKitBuilderOptions((items) => {
                const oldIndex = items.findIndex((item) => item.uuid === active.id);
                const newIndex = items.findIndex((item) => item.uuid === over.id);

                const updatedArr = arrayMove(items, oldIndex, newIndex);

                const reOrderedArr = updatedArr.map((item, index) => {
                    item.sequence = index;
                    return item;
                });

                return reOrderedArr;
            });
        }

        setActiveId(null);
        setActiveOption(null);
    };

    return (
        <div className={"CatalogItemKitBuilder"}>
            <div className={"d-flex align-items-center"}>
                <div className={"me-auto"}>
                    <OwLabel title={"kit_builder_options"} isBold={true} className={"me-2"} />
                    <OwButton title={"add"} className={"btn-warning"} leftIconClass={"fas fa-plus"} fn={addKitOption} />
                </div>

                <OwButton
                    title={openAll ? "close_all" : "open_all"}
                    className={"btn-primary no-wrap"}
                    leftIconClass={"fas fa-eye"}
                    fn={openAllFn}
                />
            </div>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                <SortableContext items={kitBuilderOptions.map((option) => option.uuid as string)} strategy={verticalListSortingStrategy}>
                    {kitBuilderOptions.map((option, index) => {
                        return (
                            <div key={option.uuid} className={"mt-2"}>
                                <CatalogItemKitBuilderOption
                                    option={option}
                                    catalogGuid={props.catalogGuid}
                                    updateFn={updateOption}
                                    openOverride={openAll}
                                    id={option.uuid as string}
                                    isActive={activeId === option.uuid}
                                />
                            </div>
                        );
                    })}
                </SortableContext>
                {/* Drag overlay to show what's being dragged */}
                <DragOverlay adjustScale={true}>
                    {activeOption && (
                        <div className="drag-overlay">
                            <CatalogItemKitBuilderOption
                                option={activeOption}
                                catalogGuid={props.catalogGuid}
                                updateFn={() => {}}
                                openOverride={false}
                                id={activeOption.uuid as string}
                                isDragOverlay={true}
                            />
                        </div>
                    )}
                </DragOverlay>
            </DndContext>
        </div>
    );
};
